/**
 * Privacy Policy translations — EN / RU / UZ
 * Each section is structured as: { title, paragraphs: [], list: [] }
 * The PrivacyPage reads from this object via the active language.
 */

export const PRIVACY_CONTENT = {
  en: {
    pageTitle: 'Your privacy. Our promise.',
    pageSub: 'How Registan Tourism collects, processes, and protects your personal information.',
    badge: 'Privacy Policy',
    lastUpdated: 'Last updated',
    onThisPage: 'On this page',
    needHelp: 'Need help?',
    needHelpSub: 'Questions about your data?',

    introduction: {
      title: '1. Introduction',
      body: [
        'Registan Tourism ("we", "us", "our") operates the website registantours.com and provides UAE visa, residency, business setup, and travel services. We respect your privacy and are committed to protecting the personal information you share with us.',
        'This Privacy Policy explains what data we collect, why we collect it, how we use and protect it, and the rights you have over your personal information. By using our website or services, you agree to the practices described in this Policy.',
      ],
    },

    dataCollect: {
      title: '2. Data We Collect',
      intro: 'We only collect data that we genuinely need to deliver our services. This includes:',
      items: [
        ['Identification documents', 'passport scans, photographs, and government-issued IDs required for visa, Emirates ID, or residency applications.'],
        ['Contact information', 'full name, phone number, WhatsApp / Telegram handle, email address, and nationality.'],
        ['Booking & transaction data', 'service selected, travel dates, application notes, payment status, and invoice records.'],
        ['Account data (if you register)', 'encrypted password, profile preferences, and saved bookings.'],
        ['Technical data', 'IP address, browser type, device information, language preference, and pages visited (collected via cookies and similar technologies).'],
        ['Communications', 'messages exchanged via our chat widget, support email, WhatsApp, or Telegram bot (@Visadubaibot).'],
      ],
    },

    howWeUse: {
      title: '3. How We Use Your Data',
      intro: 'We use your information for the following purposes:',
      items: [
        'Processing your visa, residency, business setup, or travel applications with UAE authorities.',
        'Communicating booking confirmations, status updates, and approved documents via email, WhatsApp, or Telegram.',
        'Issuing invoices, payment receipts, and tax records as required by UAE law.',
        'Providing customer support and responding to your enquiries.',
        'Improving our website, services, and user experience.',
        'Complying with legal, regulatory, or governmental obligations in the UAE and your country of residence.',
        'Preventing fraud, enforcing our terms, and protecting the safety of our users.',
      ],
      footer: 'We will never sell or rent your personal information to third parties for marketing purposes.',
    },

    cookies: {
      title: '4. Cookies & Tracking',
      body: [
        'We use cookies and similar technologies to keep you signed in, remember your language preference, measure anonymous traffic, and improve site performance. You can disable cookies in your browser settings, but some features of the site may not function properly without them.',
        'We do not use cookies for cross-site advertising or behavioural ad-targeting.',
      ],
    },

    sharing: {
      title: '5. Data Sharing',
      intro: 'We share your data only with the trusted parties strictly necessary to deliver your service:',
      items: [
        ['UAE government departments (e.g., GDRFA, ICP)', 'for visa, Emirates ID, and residency processing.'],
        ['Authorised typing centres, medical clinics, and partner agencies', 'involved in your specific application.'],
        ['Hosting & infrastructure providers', '(e.g., cloud storage, email/SMTP, Telegram Bot API) that host our platform under strict data-protection agreements.'],
        ['Payment processors', 'for secure transaction handling (we do not store full card numbers on our servers).'],
        ['Law-enforcement or regulatory authorities', 'when legally required to do so.'],
      ],
    },

    security: {
      title: '6. Data Security',
      intro: 'We apply industry-standard safeguards to protect your information, including:',
      items: [
        'HTTPS / TLS encryption for all data transmitted between your device and our servers.',
        'Encrypted password storage (bcrypt hashing) — we never see your plain-text password.',
        'Access controls limiting which staff can view sensitive customer documents.',
        'Regular security audits and prompt patching of our infrastructure.',
      ],
      footer: 'No method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security and you share information at your own risk.',
    },

    retention: {
      title: '7. Data Retention',
      intro: 'We retain your personal data only for as long as it is necessary to deliver the services you requested and to satisfy our legal, accounting, or reporting obligations:',
      items: [
        ['Active applications', 'kept until your visa or service is fully completed.'],
        ['Completed bookings & invoices', 'retained up to 5 years to comply with UAE record-keeping rules.'],
        ['Account data', 'retained until you request deletion of your account.'],
        ['Marketing consent records', 'retained until you withdraw your consent.'],
      ],
      footer: 'You may request deletion of your data at any time (see Your Rights below).',
    },

    rights: {
      title: '8. Your Rights',
      intro: 'You have the right to:',
      items: [
        ['Access', 'a copy of the personal data we hold about you.'],
        ['Correct', 'any inaccurate or outdated information.'],
        ['Delete', 'your data, where we are not legally required to retain it.'],
        ['Restrict or object', 'to certain processing activities.'],
        ['Withdraw consent', 'for marketing communications at any time.'],
        ['Lodge a complaint', 'with a relevant UAE data-protection authority.'],
      ],
      footer: 'To exercise any of these rights, contact us at support@registantours.com. We will respond within 30 days.',
    },

    children: {
      title: "9. Children's Privacy",
      body: [
        'Our services are not intended for individuals under 18. If you are a parent or guardian sponsoring a minor for a family or dependent visa, you act as the legal representative and are responsible for the data you share on their behalf. We do not knowingly collect personal data from children outside of these guardian-led applications.',
      ],
    },

    changes: {
      title: '10. Changes to This Policy',
      body: [
        'We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. The "Last updated" date at the top of this page will always show when the latest changes took effect. For significant changes, we will additionally notify you via email or a banner on the website.',
      ],
    },

    contact: {
      title: '11. Contact Us',
      intro: 'If you have any questions about this Privacy Policy, please contact us:',
    },

    sectionLabels: {
      introduction: 'Introduction',
      dataCollect: 'Data We Collect',
      howWeUse: 'How We Use Your Data',
      cookies: 'Cookies & Tracking',
      sharing: 'Data Sharing',
      security: 'Data Security',
      retention: 'Data Retention',
      rights: 'Your Rights',
      children: "Children's Privacy",
      changes: 'Changes to This Policy',
      contact: 'Contact Us',
    },
  },

  ru: {
    pageTitle: 'Ваша приватность. Наше обещание.',
    pageSub: 'Как Registan Tourism собирает, обрабатывает и защищает вашу личную информацию.',
    badge: 'Политика конфиденциальности',
    lastUpdated: 'Последнее обновление',
    onThisPage: 'На этой странице',
    needHelp: 'Нужна помощь?',
    needHelpSub: 'Вопросы о ваших данных?',

    introduction: {
      title: '1. Введение',
      body: [
        'Registan Tourism («мы», «нас», «наш») управляет сайтом registantours.com и предоставляет услуги по оформлению виз, ВНЖ, открытию бизнеса и туристические услуги в ОАЭ. Мы уважаем вашу конфиденциальность и стремимся защищать личную информацию, которую вы передаёте нам.',
        'Настоящая Политика конфиденциальности объясняет, какие данные мы собираем, для чего, как мы их используем и защищаем, а также какие у вас есть права в отношении ваших персональных данных. Используя наш сайт или услуги, вы соглашаетесь с практикой, описанной в этой Политике.',
      ],
    },

    dataCollect: {
      title: '2. Какие данные мы собираем',
      intro: 'Мы собираем только те данные, которые действительно необходимы для оказания наших услуг. Это включает:',
      items: [
        ['Документы, удостоверяющие личность', 'сканы паспорта, фотографии и удостоверения личности, необходимые для визы, Emirates ID или ВНЖ.'],
        ['Контактная информация', 'ФИО, номер телефона, WhatsApp / Telegram, e-mail и гражданство.'],
        ['Данные о бронированиях и платежах', 'выбранная услуга, даты поездки, заметки, статус оплаты и счета.'],
        ['Учётная запись (если вы зарегистрировались)', 'зашифрованный пароль, настройки профиля и сохранённые брони.'],
        ['Технические данные', 'IP-адрес, тип браузера, информация об устройстве, язык и посещённые страницы (через файлы cookie и аналогичные технологии).'],
        ['Переписка', 'сообщения через чат, e-mail службы поддержки, WhatsApp или Telegram-бот (@Visadubaibot).'],
      ],
    },

    howWeUse: {
      title: '3. Как мы используем ваши данные',
      intro: 'Мы используем вашу информацию для следующих целей:',
      items: [
        'Оформление виз, ВНЖ, бизнеса или туристических заявлений в государственных органах ОАЭ.',
        'Отправка подтверждений бронирования, обновлений статуса и одобренных документов по e-mail, WhatsApp или Telegram.',
        'Выставление счетов, чеков и налоговых документов согласно требованиям ОАЭ.',
        'Поддержка клиентов и ответы на ваши запросы.',
        'Улучшение сайта, услуг и пользовательского опыта.',
        'Соблюдение законов и нормативных требований ОАЭ и страны вашего проживания.',
        'Предотвращение мошенничества, соблюдение условий и обеспечение безопасности пользователей.',
      ],
      footer: 'Мы никогда не продаём и не сдаём в аренду вашу личную информацию третьим лицам для маркетинговых целей.',
    },

    cookies: {
      title: '4. Файлы cookie и отслеживание',
      body: [
        'Мы используем файлы cookie и аналогичные технологии, чтобы сохранять вашу авторизацию, запоминать выбранный язык, измерять анонимный трафик и улучшать производительность сайта. Вы можете отключить cookie в настройках браузера, но некоторые функции сайта могут работать некорректно.',
        'Мы НЕ используем cookie для межсайтовой рекламы или поведенческого таргетинга.',
      ],
    },

    sharing: {
      title: '5. Передача данных третьим лицам',
      intro: 'Мы передаём ваши данные только доверенным сторонам, строго необходимым для оказания услуги:',
      items: [
        ['Государственные органы ОАЭ (GDRFA, ICP и др.)', 'для оформления виз, Emirates ID и ВНЖ.'],
        ['Авторизованные типинг-центры, медицинские клиники и партнёрские агентства', 'участвующие в вашей конкретной заявке.'],
        ['Поставщики хостинга и инфраструктуры', '(например, облачное хранилище, e-mail/SMTP, Telegram Bot API), размещающие нашу платформу по строгим соглашениям о защите данных.'],
        ['Платёжные провайдеры', 'для безопасной обработки транзакций (мы не храним полные номера карт).'],
        ['Правоохранительные или регуляторные органы', 'когда это требуется по закону.'],
      ],
    },

    security: {
      title: '6. Безопасность данных',
      intro: 'Мы применяем меры защиты в соответствии с отраслевыми стандартами, включая:',
      items: [
        'Шифрование HTTPS / TLS для всех данных, передаваемых между вашим устройством и нашими серверами.',
        'Зашифрованное хранение паролей (bcrypt) — мы никогда не видим ваш пароль в открытом виде.',
        'Контроль доступа, ограничивающий, какие сотрудники могут видеть конфиденциальные документы клиентов.',
        'Регулярные аудиты безопасности и оперативное устранение уязвимостей.',
      ],
      footer: 'Ни один способ передачи данных через интернет не является на 100% безопасным. Несмотря на наши усилия, мы не можем гарантировать абсолютную безопасность, и вы делитесь информацией на свой риск.',
    },

    retention: {
      title: '7. Срок хранения данных',
      intro: 'Мы храним ваши персональные данные только столько, сколько необходимо для оказания запрошенной услуги и выполнения юридических, бухгалтерских или отчётных обязательств:',
      items: [
        ['Активные заявки', 'хранятся до полного завершения вашей визы или услуги.'],
        ['Завершённые брони и счета', 'хранятся до 5 лет согласно правилам ОАЭ по ведению учёта.'],
        ['Учётные данные', 'хранятся до запроса об удалении аккаунта.'],
        ['Согласие на маркетинг', 'хранится до отзыва согласия.'],
      ],
      footer: 'Вы можете запросить удаление ваших данных в любое время (см. раздел «Ваши права» ниже).',
    },

    rights: {
      title: '8. Ваши права',
      intro: 'Вы имеете право:',
      items: [
        ['Доступ', 'к копии персональных данных, которые мы храним о вас.'],
        ['Исправление', 'неточной или устаревшей информации.'],
        ['Удаление', 'ваших данных, если мы не обязаны хранить их по закону.'],
        ['Ограничение или возражение', 'против определённых видов обработки.'],
        ['Отзыв согласия', 'на маркетинговые сообщения в любое время.'],
        ['Подача жалобы', 'в соответствующий орган по защите данных ОАЭ.'],
      ],
      footer: 'Чтобы воспользоваться этими правами, напишите нам на support@registantours.com. Мы ответим в течение 30 дней.',
    },

    children: {
      title: '9. Конфиденциальность детей',
      body: [
        'Наши услуги не предназначены для лиц моложе 18 лет. Если вы родитель или опекун, оформляющий семейную или иждивенческую визу для несовершеннолетнего, вы выступаете законным представителем и несёте ответственность за данные, которыми делитесь от его имени. Мы сознательно не собираем персональные данные детей за рамками таких заявок.',
      ],
    },

    changes: {
      title: '10. Изменения этой Политики',
      body: [
        'Мы можем периодически обновлять данную Политику конфиденциальности, чтобы отразить изменения в наших практиках или требованиях законодательства. Дата «Последнее обновление» вверху страницы всегда отражает дату вступления изменений в силу. О существенных изменениях мы дополнительно уведомим вас по e-mail или баннером на сайте.',
      ],
    },

    contact: {
      title: '11. Связаться с нами',
      intro: 'Если у вас есть вопросы по данной Политике, свяжитесь с нами:',
    },

    sectionLabels: {
      introduction: 'Введение',
      dataCollect: 'Какие данные мы собираем',
      howWeUse: 'Как мы используем данные',
      cookies: 'Cookie и отслеживание',
      sharing: 'Передача данных',
      security: 'Безопасность',
      retention: 'Срок хранения',
      rights: 'Ваши права',
      children: 'Конфиденциальность детей',
      changes: 'Изменения политики',
      contact: 'Контакты',
    },
  },

  uz: {
    pageTitle: "Sizning maxfiyligingiz. Bizning va'damiz.",
    pageSub: "Registan Tourism shaxsiy ma'lumotlaringizni qanday yig'adi, qayta ishlaydi va himoya qiladi.",
    badge: 'Maxfiylik siyosati',
    lastUpdated: "So'nggi yangilanish",
    onThisPage: 'Ushbu sahifada',
    needHelp: 'Yordam kerakmi?',
    needHelpSub: "Ma'lumotlaringiz haqida savollaringiz bormi?",

    introduction: {
      title: '1. Kirish',
      body: [
        "Registan Tourism (\"biz\", \"bizning\") registantours.com saytini boshqaradi va BAA viza, yashash ruxsati, biznes ochish hamda sayohat xizmatlarini taqdim etadi. Biz sizning maxfiyligingizni hurmat qilamiz va siz bilan bo'lishgan shaxsiy ma'lumotlarni himoya qilishga sodiqmiz.",
        "Ushbu Maxfiylik siyosati qanday ma'lumotlarni yig'ishimiz, nima uchun yig'ishimiz, ularni qanday ishlatishimiz va himoya qilishimizni hamda shaxsiy ma'lumotlaringiz bo'yicha sizning huquqlaringizni tushuntiradi. Saytdan yoki xizmatlardan foydalanib, siz ushbu Siyosatda tasvirlangan amaliyotga rozilik bildirasiz.",
      ],
    },

    dataCollect: {
      title: "2. Biz qanday ma'lumotlarni yig'amiz",
      intro: "Biz faqat xizmatlarimizni taqdim etish uchun haqiqatdan kerak bo'lgan ma'lumotlarni yig'amiz. Bularga quyidagilar kiradi:",
      items: [
        ["Shaxsni tasdiqlovchi hujjatlar", "viza, Emirates ID yoki yashash ruxsati arizasi uchun zarur bo'lgan pasport skanlari, fotosuratlar va davlat ID lari."],
        ["Aloqa ma'lumotlari", "to'liq ism, telefon raqami, WhatsApp / Telegram, elektron pochta va fuqarolik."],
        ["Bron va to'lov ma'lumotlari", "tanlangan xizmat, sayohat sanalari, ariza eslatmalari, to'lov holati va hisob-fakturalar."],
        ["Hisob ma'lumotlari (ro'yxatdan o'tsangiz)", "shifrlangan parol, profil sozlamalari va saqlangan bronlar."],
        ["Texnik ma'lumotlar", "IP-manzil, brauzer turi, qurilma haqida ma'lumot, til va ko'rilgan sahifalar (cookie va shu kabi texnologiyalar orqali)."],
        ["Yozishmalar", "chat, qo'llab-quvvatlash elektron pochtasi, WhatsApp yoki Telegram bot (@Visadubaibot) orqali xabarlar."],
      ],
    },

    howWeUse: {
      title: "3. Ma'lumotlardan qanday foydalanamiz",
      intro: "Biz ma'lumotlaringizdan quyidagi maqsadlarda foydalanamiz:",
      items: [
        "BAA hokimiyatlarida viza, yashash ruxsati, biznes yoki sayohat arizalarini qayta ishlash.",
        "Bron tasdiqlari, holat yangilanishlari va tasdiqlangan hujjatlarni email, WhatsApp yoki Telegram orqali yetkazish.",
        "BAA qonuniga muvofiq hisob-fakturalar, to'lov kvitansiyalari va soliq hujjatlarini chiqarish.",
        "Mijozlarni qo'llab-quvvatlash va so'rovlaringizga javob berish.",
        "Sayt, xizmatlar va foydalanuvchi tajribasini yaxshilash.",
        "BAA va sizning yashash mamlakatingizdagi qonuniy va tartibga soluvchi majburiyatlarga rioya qilish.",
        "Firibgarlikning oldini olish, shartlarimizni amalga oshirish va foydalanuvchilarimiz xavfsizligini ta'minlash.",
      ],
      footer: "Biz hech qachon shaxsiy ma'lumotlaringizni marketing maqsadlarida uchinchi shaxslarga sotmaymiz yoki ijaraga bermaymiz.",
    },

    cookies: {
      title: "4. Cookie va kuzatuv",
      body: [
        "Biz cookie va shu kabi texnologiyalardan foydalanib, sizni tizimga kirgan holatda ushlab turamiz, til sozlamasini eslab qolamiz, anonim trafikni o'lchaymiz va sayt ishlashini yaxshilaymiz. Cookie ni brauzer sozlamalarida o'chirishingiz mumkin, ammo ba'zi funksiyalar to'g'ri ishlamasligi mumkin.",
        "Biz cookie ni saytlararo reklama yoki xulq-atvorga asoslangan reklamalar uchun ishlatmaymiz.",
      ],
    },

    sharing: {
      title: "5. Ma'lumotlarni baham ko'rish",
      intro: "Biz ma'lumotlaringizni faqat xizmatingizni amalga oshirish uchun zarur bo'lgan ishonchli tomonlar bilan baham ko'ramiz:",
      items: [
        ["BAA davlat idoralari (GDRFA, ICP va boshqalar)", "viza, Emirates ID va yashash ruxsatini qayta ishlash uchun."],
        ["Vakolatli typing markazlari, tibbiy klinikalar va hamkor agentliklar", "sizning aniq arizangizga aloqador."],
        ["Hosting va infratuzilma provayderlari", "(masalan, bulutli saqlash, email/SMTP, Telegram Bot API) — qattiq ma'lumotlarni himoya qilish shartnomalari ostida."],
        ["To'lov provayderlari", "xavfsiz to'lov uchun (biz to'liq karta raqamlarini saqlamaymiz)."],
        ["Huquq-tartibot organlari yoki tartibga soluvchi organlar", "qonun talab qilganda."],
      ],
    },

    security: {
      title: "6. Ma'lumotlar xavfsizligi",
      intro: "Biz ma'lumotlaringizni himoya qilish uchun sanoat standartidagi himoyani qo'llaymiz:",
      items: [
        "Qurilmangiz va serverlarimiz o'rtasida uzatiladigan barcha ma'lumotlar uchun HTTPS / TLS shifrlash.",
        "Shifrlangan parol saqlash (bcrypt) — biz hech qachon parolingizni ochiq matnda ko'rmaymiz.",
        "Mijozlarning maxfiy hujjatlarini ko'rish huquqini cheklash.",
        "Muntazam xavfsizlik tekshiruvlari va infratuzilmaning operativ yangilanishi.",
      ],
      footer: "Internet orqali uzatishning hech qanday usuli 100% xavfsiz emas. Biz ma'lumotlaringizni himoya qilishga harakat qilsak ham, mutlaq xavfsizlikni kafolatlay olmaymiz va siz ma'lumotni o'z xavfingiz ostida ulashasiz.",
    },

    retention: {
      title: "7. Ma'lumotlarni saqlash muddati",
      intro: "Biz shaxsiy ma'lumotlaringizni faqat siz so'ragan xizmatni amalga oshirish va qonuniy, buxgalteriya yoki hisobot majburiyatlarini bajarish uchun zarur bo'lgan davrgacha saqlaymiz:",
      items: [
        ["Faol arizalar", "vizangiz yoki xizmat to'liq tugagunga qadar saqlanadi."],
        ["Tugatilgan bronlar va hisob-fakturalar", "BAA hisob qoidalariga muvofiq 5 yilgacha saqlanadi."],
        ["Hisob ma'lumotlari", "hisobni o'chirish so'roviga qadar saqlanadi."],
        ["Marketing roziligi", "rozilik bekor qilinmaguncha saqlanadi."],
      ],
      footer: "Istalgan vaqtda ma'lumotlaringizni o'chirishni so'rashingiz mumkin (quyidagi 'Sizning huquqlaringiz' ni ko'ring).",
    },

    rights: {
      title: "8. Sizning huquqlaringiz",
      intro: "Sizning quyidagi huquqlaringiz mavjud:",
      items: [
        ["Kirish", "biz sizning haqingizda saqlayotgan shaxsiy ma'lumotlarning nusxasiga."],
        ["Tuzatish", "noto'g'ri yoki eskirgan ma'lumotni."],
        ["O'chirish", "biz qonun bo'yicha saqlashga majbur bo'lmagan ma'lumotlaringizni."],
        ["Cheklash yoki e'tiroz", "ayrim qayta ishlash faoliyatlariga."],
        ["Rozilikni qaytarib olish", "marketing aloqalari uchun istalgan vaqtda."],
        ["Shikoyat berish", "tegishli BAA ma'lumotlarni himoya qilish organiga."],
      ],
      footer: "Ushbu huquqlardan foydalanish uchun support@registantours.com manziliga yozing. 30 kun ichida javob beramiz.",
    },

    children: {
      title: "9. Bolalar maxfiyligi",
      body: [
        "Bizning xizmatlarimiz 18 yoshdan kichiklar uchun mo'ljallanmagan. Agar siz oilaviy yoki qaramoqdagi viza uchun voyaga yetmagan bola uchun ariza beruvchi ota-ona yoki vasiy bo'lsangiz, siz qonuniy vakil sifatida ish ko'rasiz va uning nomidan bo'lishadigan ma'lumotlar uchun javobgar bo'lasiz. Biz bunday vasiylar tomonidan boshqariladigan arizalardan tashqari bolalardan shaxsiy ma'lumotlarni bilib turib yig'maymiz.",
      ],
    },

    changes: {
      title: "10. Ushbu siyosatga o'zgartirishlar",
      body: [
        "Biz amaliyotimiz yoki qonuniy talablardagi o'zgarishlarni aks ettirish uchun ushbu Maxfiylik siyosatini vaqti-vaqti bilan yangilab turishimiz mumkin. Sahifa yuqorisidagi 'So'nggi yangilanish' sanasi har doim eng so'nggi o'zgarishlar kuchga kirgan vaqtni ko'rsatadi. Muhim o'zgarishlar haqida sizni email yoki sayt bannerи orqali qo'shimcha ravishda xabardor qilamiz.",
      ],
    },

    contact: {
      title: "11. Biz bilan bog'laning",
      intro: "Ushbu Maxfiylik siyosati bo'yicha savollaringiz bo'lsa, biz bilan bog'laning:",
    },

    sectionLabels: {
      introduction: 'Kirish',
      dataCollect: "Yig'iladigan ma'lumotlar",
      howWeUse: "Ma'lumotlardan foydalanish",
      cookies: 'Cookie va kuzatuv',
      sharing: "Ma'lumotlarni baham ko'rish",
      security: 'Xavfsizlik',
      retention: 'Saqlash muddati',
      rights: 'Huquqlaringiz',
      children: 'Bolalar maxfiyligi',
      changes: "Siyosat o'zgarishlari",
      contact: "Bog'lanish",
    },
  },
};
